<template>
  <div>
    <Section>
      <SectionHeader :title="$t('apps.billing.message.paymentmethod')"/>
      <FormRow>
        <Col className="col-lg-12 mt-2 mb-1">
        <BaseRadioPaymentMethod
          v-model="input.paymentMethod"
          @onChange="handleChangePaymentMethod"
        />
        </Col>
      </FormRow>
      <FormRow
        v-if="input.paymentMethod === BILLING_PAYMENT_METHOD.CARD"
      >
        <Col :className="['col-lg-3', inputCreditCardClassName]">
        <Label :text="`${$t('apps.billing.label.creditcardnumber')} *`" />
        <div class="input-group mb-0">
          <div class="input-group-prepend">
            <span class="input-group-text">
              <i :class="cardBrandClasses"/>
            </span>
          </div>
          <input
            v-cardformat:formatCardNumber
            ref="creditCardInput"
            :class="['form-control', inputClassName, { 'is-invalid': $v.input.creditCard.$error }]"
            v-model="$v.input.creditCard.$model"
            placeholder="#### #### #### ####"
          >
        </div>
        </Col>
        <Col :className="['col-lg-2', inputCreditCardExpireClassName]">
        <Label :text="`${$t('apps.billing.label.creditcarddateexpired')} *`" />
        <FormRow>
          <Col className="col-sm-12">
          <input
            v-cardformat:formatCardExpiry
            :class="['form-control', inputClassName, { 'is-invalid': $v.input.creditCardExpire.$error }]"
            v-model="$v.input.creditCardExpire.$model"
            placeholder="## / ####"
          >
            </Col>
        </FormRow>
        </Col>
        <Col :className="['col-lg-1', inputCreditCardCVVClassName]">
        <Label :text="`${$t('apps.billing.label.creditcardcvv')} *`" />
        <FormRow>
          <Col className="col-lg-12">
          <input
            v-cardformat:formatCardCVC
            :class="['form-control', inputClassName, { 'is-invalid': $v.input.creditCardCVV.$error }]"
            v-model="$v.input.creditCardCVV.$model"
            placeholder="###"
          >
            </Col>
        </FormRow>
        </Col>
      </FormRow>
    </Section>
    <Section
      v-if="input.paymentMethod === BILLING_PAYMENT_METHOD.CARD"
    >
      <SectionHeader :title="$t('apps.billing.message.billingaddress')"/>
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="`${$t('apps.billing.label.fullname')} *`" />
        <BaseInput
          v-model="$v.input.fullName.$model"
          :classNames="[inputClassName, { 'is-invalid': $v.input.fullName.$error }]"
        />
        </Col>
        <Col className="col-lg-3">
        <Label :text="`${$t('apps.billing.label.city')} *`" />
        <BaseInput
          v-model="$v.input.city.$model"
          :classNames="[inputClassName, { 'is-invalid': $v.input.city.$error }]"
        />
        </Col>
      </FormRow>
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="`${$t('apps.billing.label.address1')} *`" />
        <BaseInput
          v-model="$v.input.address1.$model"
          :classNames="[inputClassName, { 'is-invalid': $v.input.address1.$error }]"
        />
        </Col>
        <Col className="col-lg-3">
        <Label :text="`${$t('apps.billing.label.zipcode')} *`" />
        <BaseInput
          v-model="$v.input.zipcode.$model"
          :classNames="[inputClassName, { 'is-invalid': $v.input.zipcode.$error }]"
        />
        </Col>
      </FormRow>
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="`${$t('apps.billing.label.address2')}`" />
        <BaseInput
          v-model="input.address2"
          :className="[inputClassName]"
        />
        </Col>
      </FormRow>
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="`${$t('apps.billing.label.country')} *`" />
        <BaseDropdown
          v-model="input.countryCode"
          :options="countryOptions"
          :className="[inputClassName]"
        />
        </Col>
        <Col className="col-lg-3">
        <Label :text="`${$t('apps.billing.label.phonenumber')}`" />
        <BaseInput
          v-model="input.phoneNumber"
          :className="[inputClassName]"
        />
        </Col>
      </FormRow>
    </Section>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { cloneDeep, get } from 'lodash'
import types from 'creditcards-types'
import valid from 'card-validator'
import Card from 'creditcards/card'
import { BILLING_PAYMENT_METHOD, COUNTRY_CODE } from '../../helpers/enum'

const isValidCardTypes = (validator) => (value, vm) => {
  if (vm.paymentMethod !== BILLING_PAYMENT_METHOD.CARD) {
    return true
  }
  const { isPotentiallyValid, isValid } = validator(value)
  return isPotentiallyValid && isValid
}

const requiredIfCard = (value, vm) => {
  if (vm.paymentMethod !== BILLING_PAYMENT_METHOD.CARD) {
    return true
  }
  return required(value)
}

const DEFAULT_PAYMENT_METGOD = BILLING_PAYMENT_METHOD.PROMPTPAY

export default {
  props: {
    initialState: {
      required: true,
      type: Object,
      default: () => ({}),
    },
    isSubmitted: {
      required: true,
      type: Boolean,
      default: false,
    },
    onReset: {
      required: false,
      type: Function,
      default: null,
    },
    inputClassName: {
      type: String,
      default: null,
    },
    inputCreditCardClassName: {
      type: String,
      default: null,
    },
    inputCreditCardExpireClassName: {
      type: String,
      default: null,
    },
    inputCreditCardCVVClassName: {
      type: String,
      default: null,
    },
  },

  data () {
    return {
      BILLING_PAYMENT_METHOD,
      input: {
        creditCard: null,
        creditCardExpire: null,
        creditCardCVV: null,
        fullName: null,
        city: null,
        address1: null,
        address2: null,
        zipcode: null,
        countryCode: COUNTRY_CODE.TH,
        phoneNumber: null,
        paymentMethod: null,
      },
      inputOriginal: null,
    }
  },

  validations: {
    input: {
      creditCard: {
        requiredIfCard,
        isValidCreditCard: isValidCardTypes(valid.number),
      },
      creditCardExpire: {
        requiredIfCard,
        isValidCreditCardExpire: isValidCardTypes(valid.expirationDate),
      },
      creditCardCVV: {
        requiredIfCard,
        isValidCreditCardCVV: isValidCardTypes(valid.cvv),
      },
      fullName: {
        requiredIfCard,
      },
      city: {
        requiredIfCard,
      },
      address1: {
        requiredIfCard,
      },
      zipcode: {
        requiredIfCard,
      },
    },
  },

  computed: {
    countryOptions () {
      return [
        {
          text: 'Thailand',
          value: COUNTRY_CODE.TH,
        },
      ]
    },
    empty () {
      return this.initialState.type === null
    },
    cardBrandClasses () {
      const numberValidation = valid.number(this.input.creditCard)
      const brand = get(numberValidation, 'card.type')
      const cardBrandToClass = {
        'visa': 'fab fa-cc-visa',
        'mastercard': 'fab fa-cc-mastercard',
        'american-express': 'fab fa-cc-amex',
        'discover': 'fab fa-cc-discover',
        'diners-club': 'fab fa-cc-diners-club',
        'jcb': 'fab fa-cc-jcb',
      }
      return cardBrandToClass[brand] || 'fa fa-credit-card'
    },
  },

  watch: {
    input: {
      handler (newInput) {
        this.$emit('update:state', {
          ...newInput,
          creditCard: this.getCreditCard(),
          creditCardExpireDateMonth: this.getCreditCardExpireMonth(),
          creditCardExpireDateYear: this.getCreditCardExpireYear(),
        })
      },
      deep: true,
    },
  },

  mounted () {
    if (this.initialState) {
      this.setInput(this.initialState)
    } else {
      this.inputOriginal = cloneDeep(this.input)
    }

    this.$emit('update:form', this.$v)
    this.$emit('update:onReset', this.handleReset)
  },

  methods: {
    setInput (initState) {
      this.input.creditCard = this.getCreditCardFormated(initState.card.cardNumber)
      this.input.creditCardExpire = this.getCreditCardExpireFormated(
        initState.card.expiredDateMonth,
        initState.card.expiredDateYear
      )
      this.input.creditCardCVV = initState.card.cvvNumber
      this.input.fullName = initState.card.holderName
      this.input.city = initState.city
      this.input.address1 = initState.address1
      this.input.address2 = initState.address2
      this.input.zipcode = initState.zipcode
      this.input.countryCode = initState.countryCode
      this.input.phoneNumber = initState.phoneNumber
      this.input.paymentMethod = initState.type || DEFAULT_PAYMENT_METGOD
      this.inputOriginal = cloneDeep(this.input)
    },
    handleReset () {
      this.input = cloneDeep(this.inputOriginal)
    },
    getCreditCard () {
      return `${this.input.creditCard}`.replace(/\s/g, '') || null
    },
    getCreditCardExpireMonth () {
      try {
        const { month } = valid.expirationDate(this.input.creditCardExpire)
        return parseInt(month, 10)
      } catch (e) {
        return null
      }
    },
    getCreditCardExpireYear () {
      try {
        const { year } = valid.expirationDate(this.input.creditCardExpire)
        return parseInt(year, 10)
      } catch (e) {
        return null
      }
    },
    getCreditCardFormated (cardNumber) {
      const type = types.find(type => type.test(cardNumber, true))
      if (type !== undefined) {
        const card = Card([type])
        return card.format(cardNumber)
      }
      return cardNumber
    },
    getCreditCardExpireMonthFormated (month) {
      if (month != null && month < 10) {
        return `0${month}`
      }
      return month
    },
    getCreditCardExpireFormated (month, year) {
      if (!month && !year) {
        return null
      }
      return `${this.getCreditCardExpireMonthFormated(month) || '00'} / ${year || ''}`
    },
    handleChangePaymentMethod(value) {
      this.input.paymentMethod = value
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/base/BaseFormPaymentMethod.vue